import { useState, useEffect, useRef } from "react"
import { VectorType, isTouchEvent, isMouseEvent } from "../types/TouchOrMouseEvent"


const calculateDistance = ( e: React.TouchEvent<HTMLElement> ) =>
    Math.sqrt( Math.pow( e.touches[ 0 ].clientX - e.touches[ 1 ].clientX, 2 ) + Math.pow( e.touches[ 0 ].clientY - e.touches[ 1 ].clientY, 2 ) );


function useMoveObjectAndPinchToZoom (
    object: HTMLElement | null,
    isFlippedHorizontal: boolean,
    active: boolean = true,
    callbackSetScale: ( scale: number ) => void = () => { },
    enableMoveWithoutZoom: boolean = false //scaleが1でもmove可能にする
) {
    const [ objectMoveVector, setObjectMoveVector ] = useState( { X: 0, Y: 0 } )
    const [ scale, setScale ] = useState( 1 )

    const [ objectTouchStartCoordinates, setObjectTouchStartCoordinates ] = useState<VectorType | null>( null )
    const [ initialDistance, setInitialDistance ] = useState( 0 )
    const [ distance, setDistance ] = useState( 0 )
    const scaleRef = useRef( scale )
    const objectMoveVectorRef = useRef( objectMoveVector )
    const [ zoomCenterCoordinates, setZoomCenterCoordinates ] = useState<VectorType | null>( null )
    const objectTouchStartCoordinatesRef = useRef( objectTouchStartCoordinates )

    useEffect( () => {
        scaleRef.current = scale
        callbackSetScale( scale )
    }, [ scale ] )

    useEffect( () => {
        objectMoveVectorRef.current = objectMoveVector
    }, [ objectMoveVector ] )

    useEffect( () => {
        if ( !object ) { return }
        document.addEventListener( 'mousemove', onMouseOrTouchMove, { passive: false } )
        object.addEventListener( 'touchmove', onMouseOrTouchMove, { passive: false } )
        document.addEventListener( 'mouseup', setObjectTouchStartCoordinatesFunction )
        document.addEventListener( 'touchend', setObjectTouchStartCoordinatesFunction )
        return () => {
            // document.removeEventListener( 'mousemove', onMouseOrTouchMove, { passive: false } )
            document.removeEventListener( 'mousemove', onMouseOrTouchMove )
            object.removeEventListener( 'touchmove', onMouseOrTouchMove )
            document.removeEventListener( 'mouseup', setObjectTouchStartCoordinatesFunction )
            document.removeEventListener( 'touchend', setObjectTouchStartCoordinatesFunction )
        }
    }, [ object, objectTouchStartCoordinates ] )

    useEffect( () => {
        objectTouchStartCoordinatesRef.current = objectTouchStartCoordinates
    }, [ objectTouchStartCoordinates ] )

    const setObjectTouchStartCoordinatesFunction = () => {
        setObjectTouchStartCoordinates( null )
        document.removeEventListener( 'mousemove', onMouseOrTouchMove )
    }


    //距離が変わったらscale再計算
    useEffect( () => {
        if ( !( initialDistance && distance ) ) return
        const distanceGap = Math.abs( distance - initialDistance )
        let newScale = ( distance > initialDistance ) ?
            ( scale * distance / initialDistance ) ** 0.5 : //ズームアウト時
            ( scale * ( initialDistance + distanceGap ) / initialDistance ) ** -0.5
        if ( newScale < 1 ) {
            newScale = 1
            setObjectMoveVector( { X: 0, Y: 0 } )
        }
        if ( newScale > 2 ) return
        setScale( newScale )
    }, [ distance ] )



    const onTouchStart = ( e: React.TouchEvent<HTMLElement> ) => {
        e.preventDefault()
        e.stopPropagation()
        if ( e.touches.length === 1 ) moveTouchStart( e )
        else if ( e.touches.length === 2 ) zoomTouchStart( e )
    }

    //zoom用touchstart
    const zoomTouchStart = ( e: React.TouchEvent<HTMLElement> ) => {
        if ( !object ) return
        //2指の中間地点スクリーン絶対座標
        const X = ( e.touches[ 0 ].clientX + e.touches[ 1 ].clientX ) / 2
        const Y = ( e.touches[ 0 ].clientY + e.touches[ 1 ].clientY ) / 2
        setInitialDistance( calculateDistance( e ) )

        //2指の中間地点object座標
        const objectX = X - object.getBoundingClientRect().left
        const objectY = Y - object.getBoundingClientRect().top

        //元のtransform-origin(zoomCenterCoordinates)にはobject座標を設定
        setZoomCenterCoordinates( { X: objectX / scaleRef.current, Y: objectY / scaleRef.current } )
        setObjectTouchStartCoordinates( null )
    }

    //move用touchstart
    const moveTouchStart = ( e: React.TouchEvent<HTMLElement> ) =>
        setObjectTouchStartCoordinates( { X: e.touches[ 0 ].clientX, Y: e.touches[ 0 ].clientY } )



    //タッチかマウスが動いたら
    const onMouseOrTouchMove = ( e: MouseEvent | TouchEvent ) => {

        if ( isTouchEvent( e ) ) {
            if ( e.touches.length === 1 ) {
                if ( !enableMoveWithoutZoom && scaleRef.current === 1 ) return
                e.preventDefault()
                moveTouchMove( e )
            }
            else if ( e.touches.length === 2 ) {
                e.preventDefault()
                zoomTouchMove( e )
            }
            return
        }
        if ( !enableMoveWithoutZoom && scaleRef.current === 1 ) return
        e.preventDefault()
        isMouseEvent( e ) && moveMouseMove( e )
    }

    const moveMouseMove = ( e: React.MouseEvent<HTMLElement, MouseEvent> ) => {
        if ( !objectTouchStartCoordinatesRef.current ) return
        const currentMoveVector = {
            X: e.clientX - objectTouchStartCoordinatesRef.current.X + objectMoveVector.X,
            Y: e.clientY - objectTouchStartCoordinatesRef.current.Y + objectMoveVector.Y
        }
        setObjectMoveVector( currentMoveVector )
    }

    const moveTouchMove = ( e: React.TouchEvent<HTMLElement> ) => {
        if ( !objectTouchStartCoordinatesRef.current ) return
        const currentMoveVector = {
            X: e.touches[ 0 ].clientX - objectTouchStartCoordinatesRef.current.X + objectMoveVector.X,
            Y: e.touches[ 0 ].clientY - objectTouchStartCoordinatesRef.current.Y + objectMoveVector.Y
        }
        setObjectMoveVector( currentMoveVector )
    }

    const zoomTouchMove = ( e: React.TouchEvent<HTMLElement> ) => setDistance( calculateDistance( e ) )


    // callback(scale)
    const flipHorisontalNumber = isFlippedHorizontal ? -1 : 1

    if ( !active ) {
        return {
            style: {
                transform: `translate3d(${ objectMoveVectorRef.current.X }px ,${ objectMoveVectorRef.current.Y }px ,0) 
			${ `scale(${ flipHorisontalNumber * scale }, ${ scale })` }`,
                transformOrigin: zoomCenterCoordinates ?
                    `${ zoomCenterCoordinates.X }px ${ zoomCenterCoordinates.Y }px` :
                    "unset"
            }
        }
    }

    return {
        onMouseDown: ( e: React.MouseEvent<HTMLElement, MouseEvent> ) => setObjectTouchStartCoordinates( { X: e.clientX, Y: e.clientY } ),
        onTouchStart: ( e: React.TouchEvent<HTMLElement> ) => onTouchStart( e ),
        onMouseUp: () => setObjectTouchStartCoordinates( null ),
        style: {
            transform: `translate3d(${ objectMoveVectorRef.current.X }px ,${ objectMoveVectorRef.current.Y }px ,0) 
			${ `scale(${ flipHorisontalNumber * scale }, ${ scale })` }`,
            transformOrigin: zoomCenterCoordinates ?
                `${ zoomCenterCoordinates.X }px ${ zoomCenterCoordinates.Y }px` :
                "unset"
        }
    }
}

export default useMoveObjectAndPinchToZoom
